import { EuiButton, EuiButtonEmpty } from "@inscopix/ideas-eui";
import { useWorkflowEditorContext } from "../WorkflowEditorProvider/WorkflowEditorProvider";
import { useWorkflowEditorTimelineStatus } from "./WorkflowEditorTimeline/useWorkflowEditorTimelineStatus";
import { useState } from "react";
import assert from "assert";
import { DjangoClient } from "api/client/DjangoClient";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { useRouteMapContext } from "providers/RouteMapProvider/RouteMapProvider";
import { useTenantContext } from "providers/TenantProvider/TenantProvider";
import { WORKFLOW_EDITOR_PAGE_CONSTANTS } from "../WorkflowEditorProvider/WorkflowEditor.store";
import { captureException } from "@sentry/react";
import { keyBy } from "lodash";
import { isDefined } from "utils/isDefined";

type CreateWorkflowData = Parameters<
  typeof DjangoClient.wrs.workflow.create.queryFn
>[0];

export const WorkflowEditorBottomBar = () => {
  const [isLoadingCreate, setIsLoadingCreate] = useState(false);
  const project_id = useDataTableContext((s) => s.project.id);
  const projectKey = useDataTableContext((s) => s.project.key);
  const tenantKey = useTenantContext((s) => s.currentTenant.key);
  const { name, key } = useWorkflowEditorContext((s) => s.tableProps);

  const page = useWorkflowEditorContext((s) => s.page);

  const workflowEditorTimelineStatusMap = useWorkflowEditorTimelineStatus(page);

  const createWorkflowTable = useDataTableContext((s) => s.createWorkflowTable);
  const tablesById = useDataTableContext((s) => keyBy(s.tables, (t) => t.id));

  const navigateToTable = useRouteMapContext(
    (s) => (tableKey: string) =>
      s.routeMap["PROJECT_TABLE"]

        .dynamicPath({ projectKey, tableKey, tenantKey })

        .navigateTo(),
  );

  const onBack = useWorkflowEditorContext((s) => {
    const pageNumber =
      WORKFLOW_EDITOR_PAGE_CONSTANTS.PAGE_KEY_TO_PAGE_NUMBER[s.page];
    if (pageNumber === 1) {
      return;
    }
    const prevPageNumber = (pageNumber -
      1) as keyof typeof WORKFLOW_EDITOR_PAGE_CONSTANTS.PAGE_NUMBER_TO_PAGE_KEY;
    const prevPageKey =
      WORKFLOW_EDITOR_PAGE_CONSTANTS.PAGE_NUMBER_TO_PAGE_KEY[prevPageNumber];

    if (!workflowEditorTimelineStatusMap[prevPageKey].isDisabled) {
      return () => s.handleClickTimelinePage(prevPageKey);
    }
  });

  const onNext = useWorkflowEditorContext((s) => {
    const pageNumber =
      WORKFLOW_EDITOR_PAGE_CONSTANTS.PAGE_KEY_TO_PAGE_NUMBER[s.page];

    if (pageNumber === WORKFLOW_EDITOR_PAGE_CONSTANTS.LAST_PAGE_NUMBER) {
      return;
    }
    const nextPageNumber = (pageNumber +
      1) as keyof typeof WORKFLOW_EDITOR_PAGE_CONSTANTS.PAGE_NUMBER_TO_PAGE_KEY;
    const nextPageKey =
      WORKFLOW_EDITOR_PAGE_CONSTANTS.PAGE_NUMBER_TO_PAGE_KEY[nextPageNumber];

    if (!workflowEditorTimelineStatusMap[nextPageKey].isDisabled) {
      return () => s.handleClickTimelinePage(nextPageKey);
    }
  });

  const canSubmit = useWorkflowEditorContext((s) => {
    return (
      s.results.every(
        (result) => s.resultOverrides[result.id].outputPolicy !== undefined,
      ) &&
      s.tableProps.key !== "" &&
      s.tableProps.name !== ""
    );
  });

  const handleCreate = useWorkflowEditorContext((s) => {
    const graph = s.workflowGraph;
    if (graph === undefined) {
      return async () => new Promise((res) => setTimeout(res, 0));
    }

    return async () => {
      setIsLoadingCreate(true);

      // Source map entries are keyed by input or parameter node ID
      const mappableNodesById = keyBy(
        [...graph.inputs, ...graph.parameters],
        (node) => node.id,
      );

      const data: CreateWorkflowData = {
        project_id,
        name,
        key,
        steps: graph.steps.map<CreateWorkflowData["steps"][number]>((step) => ({
          id: step.id,
          name: s.tableInfoOverrides[step.table_id].name,
          table_key: step.table_key,
          version: s.tableInfoOverrides[step.table_id].toolVersion.id,
        })),
        edges: graph.edges,
        source_tables: s.sourceMap.sourceTableIds.map((id) => ({
          id,
          kind: (() => {
            switch (tablesById[id].kind) {
              case "data": {
                return "data_table";
              }
              case "analysis": {
                return "gdt_analysis_table";
              }
              case "workflow": {
                return "workflow_table";
              }
            }
          })(),
        })),
        param_map: Object.entries(s.sourceMap.map)
          .map<CreateWorkflowData["param_map"][number] | undefined>(
            ([nodeId, mapping]) => {
              const node = mappableNodesById[nodeId];
              assert(isDefined(node), "Param map missing node");
              if (
                mapping?.tableId === undefined ||
                mapping.columnId === undefined
              ) {
                return undefined;
              }
              return {
                step: node.table_id,
                param: node.key,
                column: {
                  id: mapping.columnId,
                  kind: (() => {
                    switch (tablesById[mapping.tableId].kind) {
                      case "data": {
                        return "data_table_column";
                      }
                      case "analysis": {
                        return "gdt_analysis_table_column";
                      }
                      case "workflow": {
                        return "workflow_table_column";
                      }
                    }
                  })(),
                },
              };
            },
          )
          .filter(isDefined),
        results: graph.results.map<CreateWorkflowData["results"][number]>(
          (result) => {
            const outputPolicy = s.resultOverrides[result.id].outputPolicy;
            assert(
              outputPolicy !== undefined,
              "Expected outputput policy to be defined",
            );
            return {
              id: result.id,
              output_policy: outputPolicy,
              intermediate: result.intermediate,
            };
          },
        ),
        inputs: graph.inputs.map<CreateWorkflowData["inputs"][number]>(
          (input) => {
            return {
              id: input.id,
              intermediate: input.intermediate,
            };
          },
        ),
      };

      try {
        const tableKey = await createWorkflowTable(data);
        // TODO add the table to the store and update the sheet engine
        addUtilityToastSuccess("Workflow created");
        navigateToTable(tableKey);
      } catch (err) {
        captureException(err);
        addUtilityToastFailure("Failed to create workflow");
      } finally {
        setIsLoadingCreate(false);
      }
    };
  });

  return (
    <>
      {onBack !== undefined && (
        <EuiButtonEmpty iconType="arrowLeft" onClick={onBack}>
          Back
        </EuiButtonEmpty>
      )}
      {page !== WORKFLOW_EDITOR_PAGE_CONSTANTS.LAST_PAGE_KEY && (
        <EuiButton fill disabled={onNext === undefined} onClick={onNext}>
          Next
        </EuiButton>
      )}

      {page === WORKFLOW_EDITOR_PAGE_CONSTANTS.LAST_PAGE_KEY && (
        <EuiButton
          isLoading={isLoadingCreate}
          fill
          disabled={!canSubmit}
          onClick={() => void handleCreate()}
        >
          Create
        </EuiButton>
      )}
    </>
  );
};
